import JwtService from '@common/services/jwt.service'
import VueAxios from 'vue-axios'
import axios from 'axios';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import router from '@common/router/router';
import {useAuthStore} from "@common/store/auth";
import {getPortailService} from "@common/services/portail.service";
import {useToastStore} from "@common/store/feature/toast/toast";


//const tracer = require('debug')('vue:apiService')

// Définir un type pour les paramètres de la méthode `query`
type QueryParams = AxiosRequestConfig; // ou affiner davantage si vous avez des paramètres spécifiques à votre API

// Définir un type pour la réponse de l'API si vous savez quel type de données vous attendez
type ApiResponse<T = any> = AxiosResponse<T>;

let API_URL = '';


const ApiService = {

    init(): Promise<void> {
        return new Promise((resolve, reject) => {
            const envPublicPath = '/env_public.json';
            //tracer('path to env_public.json', envPublicPath)

            axios.get(envPublicPath).then(({ data }) => {

                axios.defaults.headers.common['Content-Type'] = 'application/json';
                if (data && data.VITE_API_URL) {
                    API_URL = data.VITE_API_URL;
                    axios.defaults.baseURL = data.VITE_API_URL + '/'
                } else {
                    console.log('Erreur : pas de ', envPublicPath)
                    axios.defaults.baseURL = '/'
                }

                axios.interceptors.request.use(config => {
                    console.log('request to ', config.url)
                    const jeton = JwtService.getToken();
                    if (jeton) {
                        console.log('requewst with token')
                        config.headers.Authorization = `Bearer ${jeton}`;
                    }
                    return config;
                });

                axios.interceptors.response.use(response => {
                    return response;
                }, async error => {

                    console.error('Erreur dans la réponse de l\'API', error);

                    if(!error.response) {
                        console.error('Erreur de connexion au serveur');
                        await router.push({ name: 'Erreur' });
                        return Promise.reject(error);
                    }

                    if (error.response.status === 401) {

                        const refreshToken = JwtService.getRefreshToken();

                        // utilisateur non connecté
                        // => laisser le composant gérer l'erreur
                        if (!refreshToken) {
                            return Promise.reject(error);
                        }

                        // refresh token invalide/expiré
                        if (
                          error.response.data &&
                          error.response.data.error === 'refresh_token_failure'
                        ) {

                            useToastStore().ajouter({
                                text: 'Votre session a expiré. Veuillez vous reconnecter.',
                                color: 'error',
                                timeout: 5000
                            });

                            await getPortailService().deconnecter({
                                invaliderJeton: false
                            });

                            await router.push({ name: 'Connexion' });

                            return Promise.reject(error);
                        }

                        // tentative de refresh silencieuse
                        await getPortailService().rafraichirJeton();

                        return axios.request(error.config);
                    }

                    if(error.response.status === 503 && error.response.data && error.response.data.erreur === 'mode_maintenance') {

                        await router.push({ name: 'Maintenance', query: { redirect: router.currentRoute.value.fullPath } });
                        return Promise.reject(error);
                    }


                    return Promise.reject(error);
                });

                resolve();
            }).catch(err => reject(err));
        });


    },


    ajouterHeader(cle: string, valeur: string): void {
        axios.defaults.headers.common[cle] = valeur;
    },

    query<T>(resource: string, params: QueryParams): Promise<ApiResponse<T>> {
        return axios.get(resource, params).catch(error => {
            throw new Error(`[KT] ApiService ${error}`);
        });
    },

    /**
     * Send the GET HTTP request
     * @param resource
     * @returns {*}
     */
    get<T>(resource: string): Promise<ApiResponse<T>> {
        return axios.get(resource).catch(error => {
            throw new Error(`[KT] ApiService ${error}`);
        });
    },

    /**
     * Send the GET HTTP request
     * @param resource
     * @returns {*}
     */
    get<T>(resource: string, params: any): Promise<ApiResponse<T>> {
        return axios.get(resource, params).catch(error => {
            console.error(`[KT] ApiService ${error}`);
            throw error.response;
        });
    },

    /**
     * Set the POST HTTP request
     * @param resource
     * @param params
     * @returns {*}
     */
    post<T>(resource: string, params: any, options?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        return axios.post(resource, params, options).catch(error => {
            console.error(`[KT] ApiService ${error}`);
            throw error.response;
        })
    },

    /**
     * Send the UPDATE HTTP request
     * @param resource
     * @param slug
     * @param params
     * @returns {IDBRequest<IDBValidKey> | Promise<void>}
     */
    update<T>(resource: string, slug: string, params: any): Promise<ApiResponse<T>> {
        return axios.put(`${resource}/${slug}`, params);
    },

    /**
     * Send the PUT HTTP request
     * @param resource
     * @param params
     * @returns {IDBRequest<IDBValidKey> | Promise<void>}
     */
    put<T>(resource: string, params: any, extra?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        return axios.put(resource, params, extra).catch(error => {
            console.error(`[KT] ApiService ${error}`);
            throw error.response;
        });
    },

    patch<T>(resource: string, params: any): Promise<ApiResponse<T>> {
        return axios.patch(resource, params).catch(error => {
            console.error(`[KT] ApiService ${error}`);
            throw error.response;
        });
    },


    delete<T>(resource: string): Promise<ApiResponse<T>> {
        return axios.delete(resource).catch(error => {
            console.error(`[KT] ApiService ${error}`);
            throw error;
        });
    },


    async getblob(resource: string): Promise<AxiosResponse<Blob>> {
        const url = resource;
        return axios({
            url: url,
            method: 'GET',
            responseType: 'blob'
        }).catch(error => {
            throw new Error(`RestApiService ${error} : ${url}`);
        });
    },

    async postblob(resource: string, params: any): Promise<AxiosResponse<Blob>> {
        const url = resource;
        return axios({
            url: url,
            method: 'POST',
            data: params,
            responseType: 'blob'
        }).catch(error => {
            throw new Error(`RestApiService ${error} : ${url}`);
        });
    },


     sse(url: string): EventSource {
        return new EventSource(API_URL + url);
    },

    async downloadFile(url: string) {
        const response = await this.get(url, { responseType: 'blob'});

        const disposition = response.headers['content-disposition'];
        let filename = 'downloaded_file';

        if (disposition) {
            const match = disposition.match(/filename="?([^"]+)"?/);
            if (match && match[1]) {
                filename = match[1];
            }
        }

        const blob = new Blob([response.data], { type: response.headers['content-type'] });

        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;

        document.body.appendChild(link);
        link.click();

        link.remove();
        URL.revokeObjectURL(link.href);


    },

    downloadBlob(blob: Blob, filename: string) {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;

        document.body.appendChild(link);
        link.click();

        link.remove();
        URL.revokeObjectURL(link.href);
    }
}

export default ApiService;
